import type { Distribution as Dist, OptionKey } from '@examatron/shared';

/**
 * Aggregate answer distribution. SPEC §4.3.
 *
 * Counts only — there is no participant identity anywhere in this component's
 * inputs, because the wire never carries one. This is what powers "most of you
 * picked C, let's talk about why C is wrong."
 */
export function Distribution({
  options,
  distribution,
  correct,
}: {
  options: OptionKey[];
  distribution: Dist;
  correct: OptionKey[];
}) {
  const counts = options.map((key) => distribution[key] ?? 0);
  const total = counts.reduce((a, b) => a + b, 0);
  // Scaled against the most-picked option rather than the total, so a spread of
  // small numbers is still readable from the back of a room.
  const peak = Math.max(1, ...counts);

  return (
    <div className="dist">
      {options.map((key, i) => {
        const n = counts[i] ?? 0;
        const isRight = correct.includes(key);
        return (
          <div className={`dist__row${isRight ? ' dist__row--right' : ''}`} key={key}>
            <span className="tabular" style={{ fontWeight: isRight ? 600 : 400 }}>
              {key}
              {isRight ? ' ✓' : ''}
            </span>
            <span className="dist__track">
              <span className="dist__fill" style={{ width: `${(n / peak) * 100}%` }} />
            </span>
            <span className="tabular" style={{ textAlign: 'right' }}>
              {n}
            </span>
          </div>
        );
      })}
      <p style={{ marginTop: 'var(--s-2)', fontSize: 13, color: 'var(--slate)' }}>
        {total === 1 ? '1 answer' : `${total} answers`}
      </p>
    </div>
  );
}
